import React, {createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef} from 'react'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {Auth} from 'aws-amplify'

/** Do not show health toasts for background tabs; avoids stacked notifications when the user returns. */
function isHealthToastContextActive(): boolean {
  if (typeof document === 'undefined') {
    return true
  }
  return !document.hidden && document.visibilityState === 'visible'
}

// Health check status types
export interface HealthStatus {
  name: string
  url: string
  status: 'healthy' | 'unhealthy' | 'checking' | 'unknown'
  lastCheck: Date | null
  error?: string
  responseTime?: number
}

export interface HealthCheckContextType {
  healthStatus: Record<string, HealthStatus>
  isOverallHealthy: boolean
  checkHealth: (serviceName?: string) => Promise<void>
  isLoading: boolean
}

// Create context
const HealthCheckContext = createContext<HealthCheckContextType | undefined>(undefined)

// Hook to use health check context
export const useHealthCheck = () => {
  const context = useContext(HealthCheckContext)
  if (!context) {
    throw new Error('useHealthCheck must be used within a HealthCheckProvider')
  }
  return context
}

interface EndpointConfig {
  name: string
  url: string
  timeout: number
  /**
   * Attach a Cognito ID token when checking this endpoint, matching how each backend expects it
   * elsewhere in the app: 'bearer' -> `Authorization: Bearer <token>` (cloud.biobam.com, WES),
   * 'raw' -> `Authorization: <token>` (api.omicsai.biobam.com). Omit for endpoints meant to be
   * publicly reachable without auth.
   */
  auth?: 'bearer' | 'raw'
}

// Health check configuration - Production endpoints only
const HEALTH_CHECK_CONFIG: Record<string, EndpointConfig> = {
  observerApi: {
    name: 'Observer API',
    url: 'https://observer.biobam.com/api/v1/status',
    timeout: 10000,
  },
  cloudFilesApi: {
    name: 'Cloud Files API',
    // Bare API root has no route mapped (always 403 Missing Authentication Token,
    // authenticated or not) - hit a real lightweight authenticated resource instead.
    url: 'https://cloud.biobam.com/v2/storage/usage',
    timeout: 10000,
    auth: 'bearer',
  },
  sharedStorage: {
    name: 'Shared Storage',
    url: 'https://storage.biobam.com/shared/',
    timeout: 10000,
  },
  wesApi: {
    name: 'WES API',
    // This API has no CORS/OPTIONS support configured, so an authenticated cross-origin
    // request fails outright (no status code at all) rather than a clean 401/403 - stay
    // unauthenticated here instead of turning this into a permanent false "unhealthy".
    url: 'https://ee66ege16i.execute-api.us-east-2.amazonaws.com/prod/',
    timeout: 10000,
  },
  cloudWatchLogs: {
    name: 'CloudWatch Logs',
    // Reachability only - this is a raw AWS service endpoint that needs SigV4-signed
    // requests, so there's no simple authenticated call to make here.
    url: 'https://logs.us-east-2.amazonaws.com',
    timeout: 10000,
  },
  bedrockWebSocket: {
    name: 'Bedrock WebSocket',
    url: process.env.REACT_APP_BEDROCK_WS_URL || 'wss://ws.omicsai.biobam.com',
    timeout: 10000,
  },
  agentApi: {
    name: 'Agent API',
    // Real read-only route from chatApi.ts, same header convention (raw token, no "Bearer").
    url: `${process.env.REACT_APP_CHAT_API_URL || 'https://api.omicsai.biobam.com'}/conversations`,
    timeout: 10000,
    auth: 'raw',
  },
  wordpressApi: {
    name: 'WordPress API',
    url: 'https://www.biobam.com/wp-json/wp/v2/posts?per_page=1',
    timeout: 10000,
  },
}

// Health check functions
const performHttpHealthCheck = async (config: EndpointConfig): Promise<HealthStatus> => {
  const startTime = Date.now()

  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), config.timeout)

    const headers: Record<string, string> = {}
    if (config.auth) {
      try {
        const session = await Auth.currentSession()
        const token = session.getIdToken().getJwtToken()
        headers.Authorization = config.auth === 'bearer' ? `Bearer ${token}` : token
      } catch {
        clearTimeout(timeoutId)
        return {
          name: config.name,
          url: config.url,
          status: 'unknown',
          lastCheck: new Date(),
          error: 'Sign in required to check this service',
          responseTime: Date.now() - startTime,
        }
      }
    }

    const response = await fetch(config.url, {
      // Authenticated checks hit a real resource and need the actual status code back
      // (HEAD isn't mapped on these APIs); unauthenticated ones stay HEAD + no-cors since
      // they're only checking reachability, not reading a specific response.
      method: config.auth ? 'GET' : 'HEAD',
      signal: controller.signal,
      mode: config.auth ? 'cors' : 'no-cors',
      headers,
    })

    clearTimeout(timeoutId)
    const responseTime = Date.now() - startTime

    if (config.auth && !response.ok) {
      return {
        name: config.name,
        url: config.url,
        status: 'unhealthy',
        lastCheck: new Date(),
        error: `HTTP ${response.status}`,
        responseTime,
      }
    }

    return {
      name: config.name,
      url: config.url,
      status: 'healthy',
      lastCheck: new Date(),
      responseTime,
    }
  } catch (error) {
    return {
      name: config.name,
      url: config.url,
      status: 'unhealthy',
      lastCheck: new Date(),
      error: error instanceof Error ? error.message : 'Unknown error',
      responseTime: Date.now() - startTime,
    }
  }
}

const performWebSocketHealthCheck = async (
  config: typeof HEALTH_CHECK_CONFIG[keyof typeof HEALTH_CHECK_CONFIG]
): Promise<HealthStatus> => {
  const startTime = Date.now()

  return new Promise((resolve) => {
    const finish = (status: HealthStatus) => {
      if (!status.responseTime) {
        status.responseTime = Date.now() - startTime
      }
      resolve(status)
    }

    void (async () => {
      try {
        // Bedrock $connect requires Cognito ?auth= — without it API Gateway returns 403.
        let wsUrl = config.url
        if (config.name === 'Bedrock WebSocket') {
          try {
            const session = await Auth.currentSession()
            const idToken = session.getIdToken().getJwtToken()
            const url = new URL(config.url)
            url.searchParams.set('auth', idToken)
            wsUrl = url.toString()
          } catch {
            finish({
              name: config.name,
              url: config.url,
              status: 'unknown',
              lastCheck: new Date(),
              error: 'Sign in required to test WebSocket',
            })
            return
          }
        }

        const ws = new WebSocket(wsUrl)
        let settled = false

        const settle = (status: HealthStatus) => {
          if (settled) return
          settled = true
          clearTimeout(timeoutId)
          try {
            ws.close()
          } catch {
            // ignore
          }
          finish(status)
        }

        const timeoutId = setTimeout(() => {
          settle({
            name: config.name,
            url: config.url,
            status: 'unhealthy',
            lastCheck: new Date(),
            error: 'Connection timeout',
          })
        }, config.timeout)

        ws.onopen = () => {
          settle({
            name: config.name,
            url: config.url,
            status: 'healthy',
            lastCheck: new Date(),
          })
        }

        ws.onerror = () => {
          settle({
            name: config.name,
            url: config.url,
            status: 'unhealthy',
            lastCheck: new Date(),
            error: 'WebSocket connection failed',
          })
        }

        ws.onclose = (event) => {
          if (settled) return
          settle({
            name: config.name,
            url: config.url,
            status: 'unhealthy',
            lastCheck: new Date(),
            error: `Connection closed (${event.code}${event.reason ? `: ${event.reason}` : ''})`,
          })
        }
      } catch (error) {
        finish({
          name: config.name,
          url: config.url,
          status: 'unhealthy',
          lastCheck: new Date(),
          error: error instanceof Error ? error.message : 'Unknown error',
        })
      }
    })()
  })
}

interface HealthCheckProviderProps {
  children: ReactNode
  checkInterval?: number // in milliseconds
  enableNotifications?: boolean
}

export const HealthCheckProvider: React.FC<HealthCheckProviderProps> = ({
  children,
  checkInterval = 30000, // 30 seconds
  enableNotifications = true,
}) => {
  const [healthStatus, setHealthStatus] = useState<Record<string, HealthStatus>>({})
  const [isLoading, setIsLoading] = useState(false)
  /**
   * Ref keeps the last overall health without re-creating `checkHealth` on every poll.
   * (Putting the previous value in `useCallback` deps used to re-run the interval effect and
   * fire duplicate checks and toasts.)
   */
  const lastOverallStatusRef = useRef<boolean | null>(null)

  // Perform health checks
  const checkHealth = useCallback(
    async (serviceName?: string) => {
      setIsLoading(true)

      const endpoints = serviceName
        ? {[serviceName]: HEALTH_CHECK_CONFIG[serviceName as keyof typeof HEALTH_CHECK_CONFIG]}
        : HEALTH_CHECK_CONFIG

      // Filter out undefined values
      const filteredEndpoints = Object.fromEntries(
        Object.entries(endpoints).filter(([_, config]) => config !== undefined)
      ) as Record<string, typeof HEALTH_CHECK_CONFIG[keyof typeof HEALTH_CHECK_CONFIG]>

      const newStatus: Record<string, HealthStatus> = {}

      // Set all endpoints to checking status
      Object.keys(filteredEndpoints).forEach((key) => {
        const config = filteredEndpoints[key]
        newStatus[key] = {
          name: config.name,
          url: config.url,
          status: 'checking',
          lastCheck: null,
        }
      })

      setHealthStatus((prev) => ({...prev, ...newStatus}))

      // Perform checks
      const checkPromises = Object.entries(filteredEndpoints).map(async ([key, config]) => {
        const isWebSocket = config.url.startsWith('ws://') || config.url.startsWith('wss://')
        const status = isWebSocket
          ? await performWebSocketHealthCheck(config)
          : await performHttpHealthCheck(config)

        return [key, status] as [string, HealthStatus]
      })

      const results = await Promise.all(checkPromises)

      // Update status
      const updatedStatus: Record<string, HealthStatus> = {}
      results.forEach(([key, status]) => {
        updatedStatus[key] = status
      })

      setHealthStatus((prev) => ({...prev, ...updatedStatus}))
      setIsLoading(false)

      const currentOverallStatus = Object.values(updatedStatus).every(
        (s) => s.status === 'healthy' || s.status === 'unknown'
      )

      // Check for status changes and show notifications (active tab only)
      if (enableNotifications) {
        const previous = lastOverallStatusRef.current
        const canToast = isHealthToastContextActive()
        if (
          canToast &&
          previous !== null &&
          previous !== currentOverallStatus
        ) {
          if (currentOverallStatus) {
            toast.success('All services are now available', toastDefaultConfig)
          } else {
            const unhealthyServices = Object.values(updatedStatus)
              .filter((s) => s.status === 'unhealthy')
              .map((s) => s.name)
              .join(', ')
            toast.error(`Service issues detected: ${unhealthyServices}`, toastDefaultConfig)
          }
        }
      }

      // Always align ref so the next run compares against the latest result (including background polls)
      lastOverallStatusRef.current = currentOverallStatus
    },
    [enableNotifications]
  )

  // Calculate overall health
  const isOverallHealthy =
    Object.values(healthStatus).length > 0 &&
    Object.values(healthStatus).every(
      (status) => status.status === 'healthy' || status.status === 'unknown'
    )

  // Create context value
  const contextValue: HealthCheckContextType = {
    healthStatus,
    isOverallHealthy,
    checkHealth,
    isLoading,
  }

  // Initial health check and periodic checks
  useEffect(() => {
    checkHealth()

    const intervalId = setInterval(() => {
      checkHealth()
    }, checkInterval)

    return () => clearInterval(intervalId)
  }, [checkInterval, checkHealth])

  return <HealthCheckContext.Provider value={contextValue}>{children}</HealthCheckContext.Provider>
}
